import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
  ApplicationType,
  NewContractFormValues,
  NewContractOrg,
  NewcontractOrgEmployment,
} from '../../types/Application/newContract';
import {
  EmploymentProofType,
  ApplicationAddressType,
  NewContractApplicationType,
  SigningType,
  TypeOfFile,
} from '../../constants/Enum';
import {
  EmploymentByMonth,
  FormOfEmployment,
  Worksite,
} from '../../types/Application/application';
import { ValidatedField } from '../../types/validation';
import { validateField } from '../../validation';
import { validFileTypes } from '../../fileConfig';
import {
  getEmploymentsByMonth,
  getWorkedMonthsEmploymentCert,
  getWorkedMonthsWageSlips,
} from '../../Functions/Application';
import { tsl_projektdeltagande_tsl_anstallningsform } from '../../types/Enums/Generated/tsl_projektdeltagande_tsl_anstallningsform';
import { normalizePhoneNumber } from '../../utils/validation';

interface NewContractState {
  form: NewContractFormValues;
  applicationType: ApplicationType | undefined;
  employments: NewcontractOrgEmployment;
  employmentsByMonth: EmploymentByMonth[];
  signingType: SigningType | undefined;
  formOfEmployment: FormOfEmployment | undefined;
  isSwedishAddress: boolean;
  didNotFindLatestWorksite: boolean;
  isLatestWorksiteSwedishAddress: boolean;
  temporaryEmploymentHasExpired: boolean;
  moreInfo: string;
  isForaApproved: boolean;
  isSameEmailForContacts: boolean;
  isSameTelephoneForContacts: boolean;
}

const initialState: NewContractState = {
  applicationType: undefined,
  employments: {},
  form: {
    latestWorksiteOrgNumber: {
      name: 'latestWorksiteOrgNumber',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'ORGNUMBER',
      autocomplete: 'organization-number',
      formPaginationNumber: 1,
      applicationTypes: [
        NewContractApplicationType.CompetenceSupportPermanentEmployed,
        NewContractApplicationType.CompetenceSupport,
        NewContractApplicationType.Illness,
        NewContractApplicationType.TemporaryEmployment,
      ],
    },
    latestWorksite: {
      name: 'latestWorksite',
      value: '',
      isDirty: false,
      isValid: false,
      required: true,
      validationType: 'TEXT',
      formPaginationNumber: 1,
      applicationTypes: [
        NewContractApplicationType.CompetenceSupportPermanentEmployed,
        NewContractApplicationType.CompetenceSupport,
        NewContractApplicationType.Illness,
        NewContractApplicationType.TemporaryEmployment,
      ],
    },
    latestWorksiteName: {
      name: 'latestWorksiteName',
      value: '',
      isDirty: false,
      isValid: false,
      required: true,
      validationType: 'TEXT',
      autocomplete: 'organization',
      formPaginationNumber: 1,
      applicationTypes: [
        NewContractApplicationType.CompetenceSupportPermanentEmployed,
        NewContractApplicationType.CompetenceSupport,
        NewContractApplicationType.Illness,
        NewContractApplicationType.TemporaryEmployment,
      ],
    },
    latestWorksiteAddress: {
      name: 'latestWorksiteAddress',
      value: '',
      isDirty: false,
      isValid: false,
      required: true,
      validationType: 'TEXT',
      autocomplete: 'address-line1',
      formPaginationNumber: 1,
      applicationTypes: [
        NewContractApplicationType.CompetenceSupportPermanentEmployed,
        NewContractApplicationType.CompetenceSupport,
        NewContractApplicationType.Illness,
        NewContractApplicationType.TemporaryEmployment,
      ],
    },
    latestWorksiteZipCode: {
      name: 'latestWorksiteZipCode',
      value: '',
      isDirty: false,
      isValid: false,
      required: true,
      validationType: 'ZIPCODE',
      autocomplete: 'postal-code',
      formPaginationNumber: 1,
      isForeignAddress: false,
      applicationTypes: [
        NewContractApplicationType.CompetenceSupportPermanentEmployed,
        NewContractApplicationType.CompetenceSupport,
        NewContractApplicationType.Illness,
        NewContractApplicationType.TemporaryEmployment,
      ],
    },
    latestWorksiteCity: {
      name: 'latestWorksiteCity',
      value: '',
      isDirty: false,
      isValid: false,
      required: true,
      validationType: 'TEXT',
      autocomplete: 'address-level2 locality',
      formPaginationNumber: 1,
      applicationTypes: [
        NewContractApplicationType.CompetenceSupportPermanentEmployed,
        NewContractApplicationType.CompetenceSupport,
        NewContractApplicationType.Illness,
        NewContractApplicationType.TemporaryEmployment,
      ],
    },
    latestWorksiteEmployedFrom: {
      name: 'latestWorksiteEmployedFrom',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'DATE',
      formPaginationNumber: 2,
      applicationTypes: [
        NewContractApplicationType.CompetenceSupportPermanentEmployed,
        NewContractApplicationType.CompetenceSupport,
        NewContractApplicationType.Illness,
        NewContractApplicationType.TemporaryEmployment,
      ],
      compareDate: '',
      compareType: 'LESS_THAN',
    },
    latestWorksiteEmployedTo: {
      name: 'latestWorksiteEmployedTo',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'DATE',
      formPaginationNumber: 2,
      applicationTypes: [
        NewContractApplicationType.Illness,
        NewContractApplicationType.TemporaryEmployment,
      ],
      compareDate: '',
      compareType: 'GREATER_THAN',
    },
    latestWorksiteNoticeDate: {
      name: 'latestWorksiteNoticeDate',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'DATE',
      formPaginationNumber: 2,
      applicationTypes: [
        NewContractApplicationType.Illness,
        NewContractApplicationType.TemporaryEmployment,
      ],
      compareDate: '',
      compareType: 'GREATER_THAN',
      compareDateTwo: '',
      compareTypeTwo: 'LESS_THAN',
    },
    latestWorksiteEmploymentFileInput: {
      name: 'latestWorksiteEmploymentFileInput',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'FILE',
      formPaginationNumber: 2,
      validFileTypes: validFileTypes,
      files: {
        validFileTypes: validFileTypes,
        files: [],
        typeOfFile: TypeOfFile.EmploymentCertificate,
      },
      applicationTypes: [
        NewContractApplicationType.CompetenceSupportPermanentEmployed,
        NewContractApplicationType.CompetenceSupport,
        NewContractApplicationType.Illness,
        NewContractApplicationType.TemporaryEmployment,
      ],
    },
    latestWorksiteWorkedPeriod: {
      name: 'latestWorksiteWorkedPeriod',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'DATE',
      formPaginationNumber: 2,
      applicationTypes: [
        NewContractApplicationType.CompetenceSupportPermanentEmployed,
        NewContractApplicationType.CompetenceSupport,
        NewContractApplicationType.Illness,
        NewContractApplicationType.TemporaryEmployment,
      ],
    },
    contactFirstname: {
      name: 'contactFirstname',
      value: '',
      isDirty: false,
      isValid: false,
      required: true,
      validationType: 'TEXT',
      autocomplete: 'given-name',
      formPaginationNumber: 2,
      applicationTypes: [
        NewContractApplicationType.Illness,
        NewContractApplicationType.TemporaryEmployment,
      ],
    },
    contactLastname: {
      name: 'contactLastname',
      value: '',
      isDirty: false,
      isValid: false,
      required: true,
      validationType: 'TEXT',
      autocomplete: 'family-name',
      formPaginationNumber: 2,
      applicationTypes: [
        NewContractApplicationType.Illness,
        NewContractApplicationType.TemporaryEmployment,
      ],
    },
    contactEmail: {
      name: 'contactEmail',
      value: '',
      isDirty: false,
      isValid: false,
      required: true,
      validationType: 'EMAIL',
      autocomplete: 'email',
      formPaginationNumber: 2,
      applicationTypes: [
        NewContractApplicationType.Illness,
        NewContractApplicationType.TemporaryEmployment,
      ],
    },
    contactTelephone: {
      name: 'contactTelephone',
      value: '',
      isDirty: false,
      isValid: false,
      required: true,
      validationType: 'PHONENUMBER',
      autocomplete: 'tel',
      formPaginationNumber: 2,
      applicationTypes: [
        NewContractApplicationType.Illness,
        NewContractApplicationType.TemporaryEmployment,
      ],
    },
    addEmploymentOrgNumber: {
      name: 'addEmploymentOrgNumber',
      value: '',
      isDirty: false,
      isValid: false,
      required: true,
      validationType: 'ORGNUMBER',
      autocomplete: 'organization-number',
      formPaginationNumber: 2,
      applicationTypes: [
        NewContractApplicationType.CompetenceSupportPermanentEmployed,
        NewContractApplicationType.CompetenceSupport,
        NewContractApplicationType.Illness,
        NewContractApplicationType.TemporaryEmployment,
      ],
    },
    addEmploymentEmployedFrom: {
      name: 'addEmploymentEmployedFrom',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'DATE',
      formPaginationNumber: 2,
      applicationTypes: [
        NewContractApplicationType.CompetenceSupportPermanentEmployed,
        NewContractApplicationType.CompetenceSupport,
        NewContractApplicationType.Illness,
        NewContractApplicationType.TemporaryEmployment,
      ],
      compareDate: '',
      compareType: 'LESS_THAN',
    },
    addEmploymentEmployedTo: {
      name: 'addEmploymentEmployedTo',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'DATE',
      formPaginationNumber: 2,
      applicationTypes: [
        NewContractApplicationType.CompetenceSupportPermanentEmployed,
        NewContractApplicationType.CompetenceSupport,
        NewContractApplicationType.Illness,
        NewContractApplicationType.TemporaryEmployment,
      ],
      compareDate: '',
      compareType: 'GREATER_THAN',
    },
    addEmploymentFileInput: {
      name: 'addEmploymentFileInput',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'FILE',
      formPaginationNumber: 2,
      validFileTypes: validFileTypes,
      files: {
        typeOfFile: TypeOfFile.EmploymentCertificate,
        files: [],
        validFileTypes: validFileTypes,
      },
      applicationTypes: [
        NewContractApplicationType.CompetenceSupportPermanentEmployed,
        NewContractApplicationType.CompetenceSupport,
        NewContractApplicationType.Illness,
        NewContractApplicationType.TemporaryEmployment,
      ],
    },
    addEmploymentWorkedPeriod: {
      name: 'addEmploymentWorkedPeriod',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'DATE',
      formPaginationNumber: 2,
      applicationTypes: [
        NewContractApplicationType.CompetenceSupportPermanentEmployed,
        NewContractApplicationType.CompetenceSupport,
        NewContractApplicationType.Illness,
        NewContractApplicationType.TemporaryEmployment,
      ],
    },
    personalnumber: {
      name: 'personalnumber',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'PERSONALNUMBER',
      autocomplete: 'on',
      formPaginationNumber: 3,
      applicationTypes: [
        NewContractApplicationType.CompetenceSupportPermanentEmployed,
        NewContractApplicationType.CompetenceSupport,
        NewContractApplicationType.Illness,
        NewContractApplicationType.TemporaryEmployment,
      ],
    },
    firstName: {
      name: 'firstName',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'TEXT',
      autocomplete: 'given-name',
      formPaginationNumber: 3,
      applicationTypes: [
        NewContractApplicationType.CompetenceSupportPermanentEmployed,
        NewContractApplicationType.CompetenceSupport,
        NewContractApplicationType.Illness,
        NewContractApplicationType.TemporaryEmployment,
      ],
    },
    lastName: {
      name: 'lastName',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'TEXT',
      autocomplete: 'family-name',
      formPaginationNumber: 3,
      applicationTypes: [
        NewContractApplicationType.CompetenceSupportPermanentEmployed,
        NewContractApplicationType.CompetenceSupport,
        NewContractApplicationType.Illness,
        NewContractApplicationType.TemporaryEmployment,
      ],
    },
    address: {
      name: 'address',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'TEXT',
      autocomplete: 'address_line1',
      formPaginationNumber: 3,
      applicationTypes: [
        NewContractApplicationType.CompetenceSupportPermanentEmployed,
        NewContractApplicationType.CompetenceSupport,
        NewContractApplicationType.Illness,
        NewContractApplicationType.TemporaryEmployment,
      ],
    },
    coAddress: {
      name: 'coAddress',
      value: '',
      isValid: false,
      isDirty: false,
      required: false,
      validationType: 'TEXT',
      autocomplete: 'address_line2',
      formPaginationNumber: 3,
      applicationTypes: [
        NewContractApplicationType.CompetenceSupportPermanentEmployed,
        NewContractApplicationType.CompetenceSupport,
        NewContractApplicationType.Illness,
        NewContractApplicationType.TemporaryEmployment,
      ],
    },
    zipCode: {
      name: 'zipCode',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'ZIPCODE',
      autocomplete: 'postal-code',
      isForeignAddress: false,
      formPaginationNumber: 3,
      applicationTypes: [
        NewContractApplicationType.CompetenceSupportPermanentEmployed,
        NewContractApplicationType.CompetenceSupport,
        NewContractApplicationType.Illness,
        NewContractApplicationType.TemporaryEmployment,
      ],
    },
    city: {
      name: 'city',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'TEXT',
      autocomplete: 'address-level2 locality',
      formPaginationNumber: 3,
      applicationTypes: [
        NewContractApplicationType.CompetenceSupportPermanentEmployed,
        NewContractApplicationType.CompetenceSupport,
        NewContractApplicationType.Illness,
        NewContractApplicationType.TemporaryEmployment,
      ],
    },
    telephone: {
      name: 'telephone',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'PHONENUMBER',
      autocomplete: 'tel',
      formPaginationNumber: 3,
      applicationTypes: [
        NewContractApplicationType.CompetenceSupportPermanentEmployed,
        NewContractApplicationType.CompetenceSupport,
        NewContractApplicationType.Illness,
        NewContractApplicationType.TemporaryEmployment,
      ],
    },
    email: {
      name: 'email',
      value: '',
      isValid: false,
      isDirty: false,
      required: true,
      validationType: 'EMAIL',
      autocomplete: 'email',
      formPaginationNumber: 3,
      applicationTypes: [
        NewContractApplicationType.CompetenceSupportPermanentEmployed,
        NewContractApplicationType.CompetenceSupport,
        NewContractApplicationType.Illness,
        NewContractApplicationType.TemporaryEmployment,
      ],
    },
  },
  employmentsByMonth: [],
  signingType: undefined,
  isForaApproved: false,
  formOfEmployment:
    tsl_projektdeltagande_tsl_anstallningsform.Tillsvidareanstlld,
  isSwedishAddress: true,
  didNotFindLatestWorksite: false,
  isLatestWorksiteSwedishAddress: true,
  temporaryEmploymentHasExpired: false,
  moreInfo: '',
  isSameEmailForContacts: false,
  isSameTelephoneForContacts: false,
};

const newContractSlice = createSlice({
  name: 'newContract',
  initialState,
  reducers: {
    setStateToInitialState: (state) => {
      return { ...initialState };
    },
    setStateToStartSectionNewContract: (state) => {
      const {
        contactEmail,
        contactLastname,
        contactFirstname,
        contactTelephone,
        latestWorksiteEmploymentFileInput,
        latestWorksiteEmployedFrom,
        latestWorksiteEmployedTo,
        latestWorksiteNoticeDate,
        latestWorksiteWorkedPeriod,
        addEmploymentEmployedFrom,
        addEmploymentEmployedTo,
        addEmploymentFileInput,
        addEmploymentOrgNumber,
        addEmploymentWorkedPeriod,
        latestWorksiteOrgNumber,
        latestWorksiteAddress,
        latestWorksiteCity,
        latestWorksiteName,
        latestWorksiteZipCode,
      } = initialState.form;
      const employmentsInitial = initialState.employments;
      const employmentsByMonthInitail = initialState.employmentsByMonth;
      const formOfEmploymentInitial = initialState.formOfEmployment;
      const didNotFindLatestWorksite = initialState.didNotFindLatestWorksite;
      return {
        ...state,
        form: {
          ...state.form,
          contactEmail,
          contactFirstname,
          contactLastname,
          contactTelephone,
          latestWorksiteAddress,
          latestWorksiteCity,
          latestWorksiteName,
          latestWorksiteZipCode,
          latestWorksiteOrgNumber,
          latestWorksiteEmployedFrom,
          latestWorksiteEmployedTo,
          latestWorksiteEmploymentFileInput,
          latestWorksiteNoticeDate,
          latestWorksiteWorkedPeriod,
          addEmploymentEmployedFrom,
          addEmploymentEmployedTo,
          addEmploymentFileInput,
          addEmploymentOrgNumber,
          addEmploymentWorkedPeriod,
        },
        employments: employmentsInitial,
        employmentsByMonth: employmentsByMonthInitail,
        formOfEmployment: formOfEmploymentInitial,
        didNotFindLatestWorksite,
      };
    },
    setStateToEmploymentSection: (state) => {
      const {
        address,
        coAddress,
        personalnumber,
        firstName,
        lastName,
        city,
        zipCode,
        telephone,
        email,
      } = initialState.form;
      return {
        ...state,
        form: {
          ...state.form,
          firstName,
          lastName,
          address,
          coAddress,
          personalnumber,
          city,
          zipCode,
          telephone,
          email,
        },
      };
    },
    setStateToMyDetailsSection: (state) => {
      const signingInitial = initialState.signingType;
      return { ...state, signing: signingInitial };
    },
    setFormValueNewContract: (state, action: PayloadAction<ValidatedField>) => {
      state.form = {
        ...state.form,
        [action.payload.name]: {
          ...state.form[action.payload.name as keyof NewContractFormValues],
          ...action.payload,
        },
      };
    },
    validateFormValueNewContract: (state, action: PayloadAction<string>) => {
      state.form = {
        ...state.form,
        [action.payload]: {
          ...state.form[action.payload as keyof NewContractFormValues],
          isValid: validateField({
            ...state.form[action.payload as keyof NewContractFormValues],
          }),
        },
      };
    },
    validateAndSetFormValueNewContract: (
      state,
      action: PayloadAction<ValidatedField>
    ) => {
      if (action.payload.validationType === 'FILE') {
        const { files, ...payloadWithoutFiles } = action.payload;
        state.form = {
          ...state.form,
          [action.payload.name]: {
            ...state.form[action.payload.name as keyof NewContractFormValues],
            payloadWithoutFiles,
            isValid: validateField({
              ...state.form[action.payload.name as keyof NewContractFormValues],
              ...action.payload,
            }),
          },
        };
      } else {
        state.form = {
          ...state.form,
          [action.payload.name]: {
            ...state.form[action.payload.name as keyof NewContractFormValues],
            ...action.payload,
            isValid: validateField({
              ...state.form[action.payload.name as keyof NewContractFormValues],
              ...action.payload,
            }),
          },
        };
      }
    },
    setStateApplicationType: (
      state,
      action: PayloadAction<ApplicationType>
    ) => {
      if (action.payload === NewContractApplicationType.TemporaryEmployment) {
        return {
          ...state,
          applicationType: action.payload,
          formOfEmployment:
            tsl_projektdeltagande_tsl_anstallningsform.Tidsbegrnsatanstlld,
        };
      } else {
        return { ...state, applicationType: action.payload };
      }
    },
    setFormOfEmployment: (
      state,
      action: PayloadAction<tsl_projektdeltagande_tsl_anstallningsform>
    ) => {
      return { ...state, formOfEmployment: action.payload };
    },
    setWorksiteLatestEmployment: (
      state,
      action: PayloadAction<{ didNotFindWorksite: boolean; worksite: Worksite }>
    ) => {
      const latestEmployment = Object.values(state.employments).find(
        (emp: NewContractOrg) => emp.isLatestEmployment
      );
      if (latestEmployment === undefined) {
        const nodeId = Date.now().toString();
        const newLatestEmployment = {
          nodeId: nodeId,
          isLatestEmployment: true,
          employmentProofType: EmploymentProofType.EmploymentCertification,
          ...action.payload.worksite,
        } as NewContractOrg;
        state.employments[nodeId] = newLatestEmployment;
        if (action.payload.didNotFindWorksite) {
          state.form.latestWorksite.isValid = true;
          state.didNotFindLatestWorksite = true;
        } else {
          state.didNotFindLatestWorksite = false;
          state.form.latestWorksiteName.isValid = true;
          state.form.latestWorksiteAddress.isValid = true;
          state.form.latestWorksiteZipCode.isValid = true;
          state.form.latestWorksiteCity.isValid = true;
        }
      } else {
        state.employments[latestEmployment.nodeId] = {
          ...state.employments[latestEmployment.nodeId],
          ...action.payload.worksite,
        } as NewContractOrg;
        if (action.payload.didNotFindWorksite) {
          state.form.latestWorksite.isValid = true;
          state.didNotFindLatestWorksite = true;
        } else {
          state.didNotFindLatestWorksite = false;
          state.form.latestWorksiteName.isValid = true;
          state.form.latestWorksiteAddress.isValid = true;
          state.form.latestWorksiteZipCode.isValid = true;
          state.form.latestWorksiteCity.isValid = true;
        }
      }
    },
    setIsForaApprovedNewContract: (state, action: PayloadAction<boolean>) => {
      return { ...state, isForaApproved: action.payload };
    },
    setIsSwedishAddressLatestEmployment: (
      state,
      action: PayloadAction<boolean>
    ) => {
      return { ...state, isLatestWorksiteSwedishAddress: action.payload };
    },
    setTemporaryEmploymentHasExpired: (
      state,
      action: PayloadAction<boolean>
    ) => {
      return { ...state, temporaryEmploymentHasExpired: action.payload };
    },
    addEmployment: (state, action: PayloadAction<NewContractOrg>) => {
      if (action.payload.wageSlip !== undefined) {
        state.employments[action.payload.nodeId] = {
          ...action.payload,
          months: getWorkedMonthsWageSlips(
            action.payload.wageSlip,
            action.payload.employedFrom!,
            action.payload.employedTo!
          ),
        };
      }
      if (action.payload.employmentCert !== undefined) {
        state.employments[action.payload.nodeId] = {
          ...action.payload,
          months: getWorkedMonthsEmploymentCert(
            action.payload.employedFrom!,
            action.payload.employedTo!
          ),
        };
      }
    },
    saveEditedEmploymentNewContract: (
      state,
      action: PayloadAction<NewContractOrg>
    ) => {
      if (state.employments[action.payload.nodeId] !== undefined) {
        if (action.payload.wageSlip !== undefined) {
          const months = getWorkedMonthsWageSlips(
            action.payload.wageSlip,
            action.payload.employedFrom!,
            action.payload.employedTo!
          );
          state.employments[action.payload.nodeId] = {
            ...action.payload,
            months,
          };
        } else if (action.payload.employmentCert !== undefined) {
          const months = getWorkedMonthsEmploymentCert(
            action.payload.employedFrom!,
            action.payload.employedTo!
          );
          state.employments[action.payload.nodeId] = {
            ...action.payload,
            months,
          };
        } else {
          state.employments[action.payload.nodeId] = {
            ...action.payload,
          };
        }
      }
    },
    clearLatestEmploymentWorksiteFields: (state) => {
      const {
        latestWorksiteName,
        latestWorksiteAddress,
        latestWorksiteZipCode,
        latestWorksiteCity,
      } = state.form;

      const fieldNames: string[] = [
        latestWorksiteName.name,
        latestWorksiteAddress.name,
        latestWorksiteZipCode.name,
        latestWorksiteCity.name,
      ];

      fieldNames.forEach((fieldName) => {
        state.form = {
          ...state.form,
          [fieldName]: {
            ...initialState.form[fieldName as keyof NewContractFormValues],
          },
        };
      });
      state.didNotFindLatestWorksite = false;
    },
    clearLatestEmploymentFileInputField: (state) => {
      return {
        ...state,
        form: {
          ...state.form,
          latestWorksiteEmploymentFileInput:
            initialState.form.latestWorksiteEmploymentFileInput,
        },
      };
    },
    clearLatestEmploymentWorkedPeriodField: (state) => {
      return {
        ...state,
        form: {
          ...state.form,
          latestWorksiteWorkedPeriod:
            initialState.form.latestWorksiteWorkedPeriod,
        },
      };
    },
    clearAddEmploymentFileInputField: (state) => {
      return {
        ...state,
        form: {
          ...state.form,
          addEmploymentFileInput: initialState.form.addEmploymentFileInput,
        },
      };
    },
    clearAddEmploymentWorkedPeriodField: (state) => {
      return {
        ...state,
        form: {
          ...state.form,
          addEmploymentWorkedPeriod:
            initialState.form.addEmploymentWorkedPeriod,
        },
      };
    },
    clearAddEmploymentFormFields: (state) => {
      const {
        addEmploymentEmployedFrom,
        addEmploymentEmployedTo,
        addEmploymentOrgNumber,
        addEmploymentWorkedPeriod,
        addEmploymentFileInput,
      } = state.form;

      const fieldNames: string[] = [
        addEmploymentEmployedFrom.name,
        addEmploymentEmployedTo.name,
        addEmploymentOrgNumber.name,
        addEmploymentWorkedPeriod.name,
        addEmploymentFileInput.name,
      ];

      fieldNames.forEach((fieldName) => {
        state.form = {
          ...state.form,
          [fieldName]: {
            ...initialState.form[fieldName as keyof NewContractFormValues],
          },
        };
      });
    },
    setEmploymentsByMonthNewContract: (state) => {
      const employments = Object.values(state.employments);
      state.employmentsByMonth = getEmploymentsByMonth(employments);
    },
    deleteEmploymentNewContract: (state, action: PayloadAction<string>) => {
      delete state.employments[action.payload];
    },
    setIsSwedishAddress: (
      state,
      action: PayloadAction<ApplicationAddressType>
    ) => {
      return {
        ...state,
        isSwedishAddress:
          action.payload === ApplicationAddressType.SwedishAddress,
      };
    },
    setMoreInfoNewContract: (state, action: PayloadAction<string>) => {
      return { ...state, moreInfo: action.payload };
    },
    setSigningNewContract: (state, action: PayloadAction<SigningType>) => {
      return { ...state, signingType: action.payload };
    },
    validateCompanyContactAndParticipantEmail: (state) => {
      const { form } = state;
      const companyEmail = form.contactEmail;
      const participantEmail = form.email;

      if (companyEmail.isDirty && participantEmail.isDirty) {
        if (
          companyEmail.value.trim().toLowerCase() ===
          participantEmail.value.trim().toLowerCase()
        ) {
          companyEmail.isValid = false;
          participantEmail.isValid = false;
          state.isSameEmailForContacts = true;
        } else {
          state.isSameEmailForContacts = false;
          companyEmail.isValid = validateField(companyEmail);
          participantEmail.isValid = validateField(participantEmail);
        }
      }
    },
    validateCompanyContactAndParticipantTelephone: (state) => {
      const { form } = state;
      const companyTelephone = form.contactTelephone;
      const participantTelephone = form.telephone;

      if (companyTelephone.isDirty && participantTelephone.isDirty) {
        if (
          normalizePhoneNumber(companyTelephone.value) ===
          normalizePhoneNumber(participantTelephone.value)
        ) {
          companyTelephone.isValid = false;
          participantTelephone.isValid = false;
          state.isSameTelephoneForContacts = true;
        } else {
          state.isSameTelephoneForContacts = false;
          companyTelephone.isValid = validateField(companyTelephone);
          participantTelephone.isValid = validateField(participantTelephone);
        }
      }
    },
  },
});

export const {
  setStateApplicationType,
  setStateToStartSectionNewContract,
  setStateToEmploymentSection,
  setStateToMyDetailsSection,
  validateAndSetFormValueNewContract,
  setFormValueNewContract,
  validateFormValueNewContract,
  setWorksiteLatestEmployment,
  setIsForaApprovedNewContract,
  setIsSwedishAddressLatestEmployment,
  setTemporaryEmploymentHasExpired,
  addEmployment,
  saveEditedEmploymentNewContract,
  clearLatestEmploymentWorksiteFields,
  clearLatestEmploymentFileInputField,
  clearLatestEmploymentWorkedPeriodField,
  clearAddEmploymentFileInputField,
  clearAddEmploymentWorkedPeriodField,
  clearAddEmploymentFormFields,
  setEmploymentsByMonthNewContract,
  deleteEmploymentNewContract,
  setIsSwedishAddress,
  setSigningNewContract,
  setMoreInfoNewContract,
  setFormOfEmployment,
  validateCompanyContactAndParticipantEmail,
  validateCompanyContactAndParticipantTelephone,
} = newContractSlice.actions;

export default newContractSlice.reducer;
